/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import {
  encodeUrlIfNotAlready,
  crossBrowserURLParse,
  getNamePartsFromFullName
} from "./UrlUtils";

import blackListedExtensions from "../Constants/blackListedExtensions";

const WHITELISTED_PROTOCOLS = Object.freeze([
  "http:",
  "https:",
  "tel:",
  "mailto:"
]);
const EXAMPLE_ORIGIN = "http://example.com";
const DEFAULT_PROTOCOL = "http:";

class SecurityUtils {
  static openUrl(url, allowedProtocols = WHITELISTED_PROTOCOLS) {
    if (!url) {
      return;
    }
    const result = this.getValidURLAndProtocol(url);
    if (this.isURLProtocolWhitelisted(result.protocol, allowedProtocols)) {
      this.openValidatedUrl(result.url);
    }
  }

  static sendMail(url) {
    if (!url) {
      return;
    }
    const result = this.getValidURLAndProtocol(url);
    if (result.protocol !== "mailto:") {
      return;
    }
    this.openValidatedUrl(result.url);
  }

  static openValidatedUrl(url) {
    /**
     * Put this in a try catch so we are able to remove iframe from DOM
     * even when an exception occurs.
     */
    try {
      let encodedUrl = "";
      const urlParts = crossBrowserURLParse(url);
      /* istanbul ignore else  */
      if (urlParts.origin !== "null") {
        encodedUrl =
          encodeUrlIfNotAlready(urlParts.origin + urlParts.pathname) +
          urlParts.search +
          urlParts.hash;
      } else {
        encodedUrl = encodeUrlIfNotAlready(url);
      }
      const appWindow = window.open(
        encodedUrl,
        "_blank",
        "noopener,noreferrer"
      );
      appWindow.opener = null;
    } catch (error) {
      console.error("Error openValidatedUrl", error);
    }
  }

  static getValidURLAndProtocol(url) {
    const urlObj = crossBrowserURLParse(url, EXAMPLE_ORIGIN);
    if (urlObj.origin !== EXAMPLE_ORIGIN) {
      return {
        url,
        protocol: urlObj.protocol
      };
    }
    return {
      url: `${DEFAULT_PROTOCOL}//${url}`,
      protocol: DEFAULT_PROTOCOL
    };
  }

  static isURLProtocolWhitelisted(
    protocol,
    allowedProtocols = WHITELISTED_PROTOCOLS
  ) {
    return allowedProtocols.includes(protocol);
  }

  static isAttachmentTypeBlacklisted(fileName) {
    let extension = getNamePartsFromFullName(fileName).fileExtension;
    if (extension) {
      extension = `.${extension.toLowerCase()}`;
    }
    let isBlackListed = false;
    if (
      extension.length > 0 &&
      blackListedExtensions.map(e => e.toLowerCase()).includes(extension)
    ) {
      isBlackListed = true;
    }
    return isBlackListed;
  }

  static getProtocolWhitelist() {
    return WHITELISTED_PROTOCOLS;
  }
}

export { SecurityUtils, blackListedExtensions as blackListedAttachments };
